"use client";
import React, { useMemo } from "react";
import { KeywordsPageResponse } from "./entitites/KeywordsResponse";
import { CasinoPageContext } from "./CasinoPageContext";

import Navbar from "./components/Navbar";
import HeroSection from "./Homepage-Sections/HeroSection";
import HeroContent from "./Homepage-Sections/HeroContent";
import Casino24AdSection from "./Homepage-Sections/Casino24AdSection";
import AboutSection from "./Homepage-Sections/AboutSection";
import PartnersSection from "./Homepage-Sections/PartnersSection";
import BlogsSection from "./Homepage-Sections/BlogsSection";
import FAQSection from "./Homepage-Sections/FaqSection";
import Footer from "./components/Footer";

import CasinoSubpage from "./CasinoSubpage";

interface CasinoHomepageClientProps {
  domain: string;
  subpageSlug?: string;
  website: KeywordsPageResponse | null;
}

/**
 * Normalize slugs coming from:
 * - URL
 * - Database
 * so they always match
 */
function normalizeSlug(value: string | null | undefined): string {
  return (
    (value ?? "")
      .toString()
      .trim()
      .toLowerCase()

      // common differences
      .replace(/_/g, "-")
      .replace(/\s+/g, "-")

      // remove leading/trailing slashes
      .replace(/^\/+|\/+$/g, "")
  );
}

const CasinoHomepageClient = ({
  domain,
  subpageSlug,
  website,
}: CasinoHomepageClientProps) => {
  /**
   * Determine which subpage should render
   */

  // console.log("CLIENT RECEIVED SLUG:", subpageSlug);

  const currentSubpage = useMemo(() => {
    if (!website || !subpageSlug || !website.subpages) {
      return null;
    }

    const targetSlug = normalizeSlug(subpageSlug);

    // ---- DEBUG (safe in dev) ----
    if (process.env.NODE_ENV === "development") {
      console.log("URL slug:", targetSlug);
      console.log(
        "Available slugs:",
        website.subpages.map((s) => s.slug),
      );
    }
    // -----------------------------

    return (
      website.subpages.find((sp) => normalizeSlug(sp.slug) === targetSlug) ??
      null
    );
  }, [website, subpageSlug]);

  /**
   * RENDER LOGIC
   * - if slug exists → ALWAYS try subpage
   * - only show homepage when NO slug provided
   */
  const renderContent = () => {
    // 🟢 HOMEPAGE MODE
    if (!subpageSlug) {
      return (
        <>
          <HeroSection />
          <HeroContent />
          <Casino24AdSection />
          <AboutSection />
          <PartnersSection />
          <BlogsSection />
          <FAQSection />
        </>
      );
    }

    // 🟢 SUBPAGE FOUND
    if (currentSubpage) {
      return <CasinoSubpage subpage={currentSubpage} />;
    }

    // 🔴 SUBPAGE NOT FOUND (clear feedback)
    return (
      <div className="p-20 text-center text-red-400">
        <h1 className="text-3xl font-bold mb-4">Subpage not found</h1>

        <p className="mb-4">
          Requested slug: <strong>{subpageSlug}</strong>
        </p>

        {process.env.NODE_ENV === "development" && (
          <pre className="text-left bg-black p-4 text-xs overflow-auto">
            {JSON.stringify(
              website?.subpages?.map((s) => s.slug),
              null,
              2,
            )}
          </pre>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <CasinoPageContext.Provider
        value={{ domain, website: website || undefined }}
      >
        <Navbar />

        <main id="main-content">{renderContent()}</main>

        <Footer />
      </CasinoPageContext.Provider>
    </div>
  );
};

export default CasinoHomepageClient;
